// @flow
import * as React from "react";
import { FormControl, FormHelperText } from "@mui/material";
import "react-international-phone/style.css";

import {
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  OutlinedInput,
} from "@mui/material";
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";

type Props = {
  value: string,
  onChange: (phone: string) => void,
  onBlur?: () => void,
  error?: boolean,
  helperText?: string | false,
  placeholder?: string,
  onCountryChange?: (countryId: string) => void,
};

const MuiPhoneInput = ({
  value,
  onChange,
  onBlur,
  error = false,
  helperText,
  placeholder = "Phone",
  onCountryChange,
}: Props) => {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: 'gr',
      value,
      countries: defaultCountries,
      onChange: (data) => {
        onChange(data.phone);
      },
    });

  // Handle country change
  const handleCountryChange = (iso2) => {
    // Find the selected country's dial code
    const selectedCountry = defaultCountries.find((c) => {
      const parsedCountry = parseCountry(c);
      return parsedCountry.iso2 === iso2;
    });

    if (selectedCountry && onCountryChange) {
      const { dialCode } = parseCountry(selectedCountry);
      onCountryChange(dialCode);
    }

    setCountry(iso2);
  };

  // Initialize default country on mount
  React.useEffect(() => {
    if (country && country?.dialCode) {
      onCountryChange(country?.dialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormControl variant="standard" fullWidth error={error}>
      <OutlinedInput
        placeholder={placeholder}
        type="tel"
        value={inputValue}
        name={`phone`}
        onChange={handlePhoneValueChange}
        onBlur={onBlur}
        inputRef={inputRef}
        inputProps={{
          style: {
            fontSize: "14px",
            padding: "10px",
            minHeight: "unset",
          },
        }}
        startAdornment={
          <InputAdornment
            position="start"
            style={{ marginRight: "2px", marginLeft: "-8px" }}
          >
            <Select
              MenuProps={{
                style: {
                  height: "300px",
                  width: "360px",
                  top: "10px",
                  left: "-34px",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }}
              sx={{
                width: "max-content",
                // Remove default outline (display only on focus)
                fieldset: {
                  display: "none",
                },
                '&.Mui-focused:has(div[aria-expanded="false"])': {
                  fieldset: {
                    display: "block",
                  },
                },
                // Update default spacing
                ".MuiSelect-select": {
                  padding: "0 24px 0 4px !important",
                },
                svg: {
                  right: 0,
                },
              }}
              value={country.iso2}
              onChange={(e) => {
                handleCountryChange(e.target.value);
              }}
              renderValue={(value) => (
                <FlagImage iso2={value} style={{ display: "flex" }} />
              )}
            >
              {defaultCountries?.map((c) => {
                const country = parseCountry(c);
                return (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    <FlagImage
                      iso2={country.iso2}
                      style={{ marginRight: "8px" }}
                    />
                    <Typography marginRight="8px">{country.name}</Typography>
                    <Typography color="gray">+{country.dialCode}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </InputAdornment>
        }
        fullWidth
        autoComplete="on"
        color={"grey"}
        size={"small"}
        required
        error={error}
        id="phone"
        className="border-neutral-dark-radius"
        sx={{ minHeight: "unset", margin: "0 auto 0 auto" }}
      />
      {helperText && (
        <FormHelperText error id="standard-weight-helper-text-phone">
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default MuiPhoneInput;
